import {
  type AppBundleIdOptionPayload,
  type AppInventoryOptionPayload,
  type InventoryAndContentOptionPayload,
  type InventoryAndContentState,
  TargetableInventoryOption,
} from '@openx/types/targeting/inventoryAndContent';
import {
  type ComparisonMap,
  ComparisonType,
  type OptionEntriesProps,
} from '@openx/types/targeting/targetingValuesTypes';
import { sumLengths } from '@openx/utils/lib/sumLengths';

const getComparisonsCount = (comparisonObject: InventoryAndContentOptionPayload, label: string) =>
  sumLengths(Object.values(comparisonObject?.val || []), val => (val ? val.length : 0)) ? label : null;

const inventoryAndContentSettingsCheckingMethods = {
  // [InventoryContentOption.PUBLISHER_ID]
  account(targetingSettings: InventoryAndContentState) {
    return targetingSettings.account.val.length ? 'account' : null;
  },
  adunit(targetingSettings: InventoryAndContentState) {
    return targetingSettings.adunit.val.length ? 'adunit' : null;
  },
  adunit_size(targetingSettings: InventoryAndContentState) {
    return targetingSettings.adunit_size.val.length ? 'adunit_size' : null;
  },
  app_bundle_id(targetingSettings: InventoryAndContentState) {
    const hasAnyValue =
      getComparisonsCount(targetingSettings.app_bundle_id as InventoryAndContentOptionPayload, 'app_bundle_id') ||
      targetingSettings.app_bundle_id?.inventorylist_id;

    return hasAnyValue ? 'app_bundle_id' : null;
  },
  // biome-ignore lint/suspicious/noEmptyBlockStatements: intentionally left empty
  content_inter_dimension_operator() {},
  // [InventoryContentOption.INSTANCE_HASH]
  instance(targetingSettings: InventoryAndContentState) {
    return targetingSettings.instance.val.length ? 'instance' : null;
  },
  inventory(targetingSettings: InventoryAndContentState) {
    const includes = targetingSettings.inventory[TargetableInventoryOption.INCLUDES].length;
    const excludes = targetingSettings.inventory[TargetableInventoryOption.EXCLUDES].length;

    return includes + excludes ? 'inventory' : null;
  },
  keywords(targetingSettings: InventoryAndContentState) {
    return targetingSettings.keywords.val.length ? 'keywords' : null;
  },
  line_item_app_bundle_id(targetingSettings: InventoryAndContentState) {
    return targetingSettings.line_item_app_bundle_id.val.length ? 'line_item_app_bundle_id' : null;
  },
  page_url(targetingSettings: InventoryAndContentState) {
    return getComparisonsCount(targetingSettings.page_url, 'page_url') ? 'page_url' : null;
  },
  publisher_inventory_rules(targetingSettings: InventoryAndContentState) {
    return targetingSettings.publisher_inventory_rules?.length ? 'publisher_inventory_rules' : null;
  },
  site(targetingSettings: InventoryAndContentState) {
    return targetingSettings.site.val.length ? 'site' : null;
  },
  appgroup(targetingSettings: InventoryAndContentState) {
    return getComparisonsCount(targetingSettings.appgroup as InventoryAndContentOptionPayload, 'appgroup')
      ? 'appgroup'
      : null;
  },
};
export const getInventoriesWithSettings = (targetingSettings: Partial<InventoryAndContentState>) => {
  return Object.keys(targetingSettings).filter(optionLabel =>
    inventoryAndContentSettingsCheckingMethods[optionLabel]?.(targetingSettings)
  );
};

export const getSelectedOptionsCount = (optionsEntries: Array<OptionEntriesProps>, inventoryListLength = 0): number =>
  //option[1] because its value of comparisonType object entries
  optionsEntries.flatMap(option => option[1]).length + inventoryListLength;

export const isAppInventoryPayload = (payload: unknown): payload is AppInventoryOptionPayload => {
  return typeof payload === 'object' && payload !== null && 'val' in payload && 'op' in payload;
};

export const isAppBundleIdPayload = (payload: unknown): payload is AppBundleIdOptionPayload => {
  return typeof payload === 'object' && payload !== null && 'op' in payload;
};

export const deriveComparisonType = (val?: ComparisonMap): ComparisonType => {
  if (!val) return ComparisonType.EQUALS;
  const entry = Object.entries(val).find(([, items]) => Array.isArray(items) && items.length > 0);
  return (entry?.[0] as ComparisonType) ?? ComparisonType.EQUALS;
};

// Bundle ID inherits App Name's mode when it has no items of its own.
// Invariant (enforced by AllowBlockToggle): App Name "Block" ⇒ Bundle ID "Block".
// Operates on `val` maps only so `inventorylist_id` cannot skew the derivation.
export const deriveBundleIdComparisonType = (
  bundleIdVal?: ComparisonMap,
  appGroupVal?: ComparisonMap
): ComparisonType => {
  const hasOwnItems =
    !!bundleIdVal && Object.values(bundleIdVal).some(items => Array.isArray(items) && items.length > 0);
  return hasOwnItems ? deriveComparisonType(bundleIdVal) : deriveComparisonType(appGroupVal);
};
